import { useState, useRef, useCallback, useEffect } from 'react';
import { Editor, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { getOwnerDocument } from '../../../../core/utilities/dom/getOwnerDocument.mjs';
import { hasTarget } from '../../../../core/utilities/dom/hasTarget.mjs';

function useDragDrop(editor, {
  readOnly
}) {
  // Keep track of whether the drag start event happened inside of this editor
  const [isDragging, setIsDragging] = useState(false);
  const isValidDrop = useRef(false);
  const handleDrop = useCallback(() => {
    isValidDrop.current = true;
  }, []);
  useEffect(() => {
    const ownerDocument = getOwnerDocument(editor);
    if (isDragging) {
      isValidDrop.current = false;
      ownerDocument.addEventListener('drop', handleDrop);
    }
    return () => ownerDocument.removeEventListener('drop', handleDrop);
  }, [editor, handleDrop, isDragging]);
  return {
    isDragging,
    handlers: {
      onDragStart: useCallback(event => {
        if (hasTarget(editor, event.target)) {
          setIsDragging(true);
        }
      }, [editor]),
      onDrop: useCallback(event => {
        if (!readOnly && hasTarget(editor, event.target)) {
          // Force editor contents to be normalized on drop
          Editor.normalize(editor, {
            force: true
          });

          // Make sure the editor has focus
          if (!ReactEditor.isFocused(editor)) {
            /**
             * When dragging from another source into the editor, it's possible
             * the current editor does not have focus
             */
            ReactEditor.focus(editor);
          }
        }
        setIsDragging(false);
      }, [editor, readOnly]),
      /**
       * When dropping on a different droppable element than the current editor,
       * `onDrop` is not called. So we need to clean up in `onDragEnd` instead.
       * Note: `onDragEnd` is only called when `onDrop` is not called
       */
      onDragEnd: useCallback(() => {
        if (isDragging) {
          // Delete contents from current editor when dropping onto different editor
          if (!readOnly && editor.selection && isValidDrop.current) {
            Transforms.delete(editor);
          }
          setIsDragging(false);
        }
      }, [editor, readOnly, isDragging])
    }
  };
}

export { useDragDrop };
